import { useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"

type Props = {
  entryRequestId: number
  /**
   * Where approve and reject actually go. Passed in rather than built from the
   * id, because the same two buttons now serve two namespaces: the diner route
   * (`/entry_requests/:id/approve`, apex-relative) and the staff one
   * (`staff_location_approve_entry_request_path`, staff-relative) send the same
   * path shape on different hosts. Staff's own hrefs come straight off
   * `Staff::TablesWorkspaceSerializer`, which is the whole reason it sends them.
   */
  approveHref: string
  rejectHref: string
  /**
   * Diner reads "Approve"; a waiter reads "Open the table" — the actual
   * consequence of the tap from the side that is opening a table, not just
   * answering a request. Defaults to the diner wording so the two existing
   * notification call sites need no change beyond the new hrefs.
   */
  approveLabel?: string
  /**
   * Lets a caller that renders these persistently — the diner's alert, the
   * staff floor panel — react to which action resolved. The feed only ever
   * emits the request's creation, so nothing else would ever clear it or tell
   * a waiter which button they just pressed.
   */
  onResolved?: (action: "approve" | "reject") => void
}

/**
 * The answer to "someone is standing at the table asking to be let in".
 *
 * Lives on the alert itself rather than behind a link: the whole point of this
 * flow is that a person waiting in front of an unmoving QR gets an answer
 * quickly, and sending the approver somewhere else to give it defeats that.
 *
 * Shared by every audience that can answer one — the diner's own alert, the
 * staff notification dropdown, and the staff floor's entry-request panel —
 * rather than each building its own copy of two buttons and a submitting flag.
 * Whether this particular person is actually allowed to say yes is decided
 * server-side by `Location::EntryRequest#approvable_by?` — a diner who was
 * themselves let in by an approval cannot approve the next one, and the button
 * being visible never means the answer will be accepted.
 */
export function EntryRequestActions({
  entryRequestId,
  approveHref,
  rejectHref,
  approveLabel,
  onResolved,
}: Props) {
  const [submitting, setSubmitting] = useState(false)

  const resolve = (action: "approve" | "reject") => {
    setSubmitting(true)
    router.patch(
      action === "approve" ? approveHref : rejectHref,
      {},
      {
        preserveScroll: true,
        onSuccess: () => onResolved?.(action),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <div className="flex items-center gap-2 pt-1">
      <Button
        size="sm"
        disabled={submitting}
        onClick={() => resolve("approve")}
        data-testid={`entry-request-approve-${entryRequestId}`}
      >
        {approveLabel ?? t("ui.react.features.entry_requests.approve")}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={submitting}
        onClick={() => resolve("reject")}
        data-testid={`entry-request-reject-${entryRequestId}`}
      >
        {t("ui.react.features.entry_requests.reject")}
      </Button>
    </div>
  )
}
